/**
 * ChatButton - Floating button to open the AI Financial Advisor chat
 */

import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { toggleChat } from '@/store'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Bot } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatButtonProps {
  className?: string
}

export function ChatButton({ className }: ChatButtonProps) {
  const dispatch = useAppDispatch()
  const { isOpen, messages } = useAppSelector((state) => state.chat)
  
  // Don't show button when chat is open and not minimized
  if (isOpen) return null
  
  const unreadCount = messages.filter(m => m.role === 'assistant').length
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className={cn('fixed bottom-4 right-4 z-40', className)}
    >
      <Button
        onClick={() => dispatch(toggleChat())}
        size="lg"
        className={cn(
          'h-14 w-14 rounded-full shadow-lg',
          'hover:scale-105 transition-transform'
        )}
      >
        <Bot className="h-6 w-6" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full p-0 flex items-center justify-center text-xs"
          >
            {unreadCount}
          </Badge>
        )}
      </Button>
      
      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
        className="absolute right-16 top-1/2 -translate-y-1/2 whitespace-nowrap"
      >
        <div className="bg-popover text-popover-foreground text-sm px-3 py-2 rounded-lg shadow-lg">
          Ask the AI Advisor
        </div>
      </motion.div>
    </motion.div>
  )
}
